"use client";

import { deleteCookie, getCookie, setCookie } from "cookies-next/client";
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from "react";
import LocationSelector from "src/components/locationselector/LocationSelector";
import getURLParameter from "src/lib/client/getURLParameter";
import { LocationContext, useUserContext } from "src/lib/contexts/ContextTypes";
import { LOCATIONS_SMALL } from "src/lib/contexts/smallLocations";
import { SELECTED_CINEMAS_COOKIE } from "src/lib/programHash";
const BRAND = process.env.NEXT_PUBLIC_WEBSITE_BRAND;
const ASK_FOR_LOCATION = [/^\/film\/.*/i, /^\/program(?:\/(?!sted(?:\/|$)).*)?$/i];

// set per brand in .env-cmdrc (.filmweb.no/.kino.dk), also when developing locally - a hardcoded .filmweb.no
// was rejected by the browser on local.kino.dk, so the location cookie never changed there
const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN;

//#region [Props]
type LocationWrapperProps = {
  children: React.ReactNode;
  locationSelector?: React.ReactNode; // if you want to have a custom locationselector
  canUseGeoLocation: boolean;
  predefinedLocation?: string;
};
//#endregion

//#region [Component]
export function LocationWrapper({
  children,
  locationSelector,
  canUseGeoLocation,
  predefinedLocation
}: LocationWrapperProps) {
  const router = useRouter();
  const userContext = useUserContext();
  const [selectedLocation, setSelectedLocation] = useState<string[] | null>(predefinedLocation ? [predefinedLocation] : null);
  const [isReady, setIsReady] = useState<boolean>(false);
  // derived purely from selectedLocation - no need to sync it through its own state + effect
  const showAllDates = checkIfAllDatesShouldBeShown(selectedLocation ?? []);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  useEffect(() => {
    // reads the location from the URL/cookie on mount - this is external browser state that isn't
    // available during render (would reintroduce an SSR/client hydration mismatch), so it has to
    // be synced in here rather than computed during render
    /* eslint-disable react-hooks/set-state-in-effect */
    console.group("INIT LOCATION NEW");
    let skipCookie = !!predefinedLocation;
    //console.debug("Skip cookie", skipCookie);
    let locs: string[] = [];
    // Should only be called initially.
    //console.debug("Predefined location", predefinedLocation);
    if (!predefinedLocation) {
      let locationParam = getURLParameter("location", true);
      if (locationParam) {
        locs = locationParam = Array.from(new Set(locationParam.map(lp => lp.split(":")).flat().map(lp => decodeURIComponent(lp?.trim())))).filter(c => (c?.trim() ?? "") !== "").sort();
        if (locationParam.length !== 0) {
          console.debug("Setting selected location", locationParam);
          setSelectedLocation(locationParam);
          skipCookie = true;
        }
      }
    } else {
      locs = [predefinedLocation];
    }
    //console.debug("Locs", locs);
    //console.debug("Skipcookie 2", skipCookie);
    if (!skipCookie) {
      const locCookie = getCookie("location");
      console.debug("Got loc cookie", locCookie);
      if (locCookie) {
        locs = Array.from(new Set((locCookie ?? "").split(":"))).filter(c => (c?.trim() ?? "") !== "").sort();
        console.debug("Setting selected location", locs);
        setSelectedLocation(locs);
      } else {
        setSelectedLocation([]);
      }
    }

    // before setIsReady: SelectedCinemaWrapper reads the cinemas cookie once the location is ready
    wipeSelectedCinemasIfNewLocation(locs);
    setIsReady(true);
    console.groupEnd();
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [predefinedLocation]);
  useEffect(() => {
    // when selected location changes, update the cookie and the url
    if (selectedLocation) {
      console.debug("Selected location", selectedLocation, predefinedLocation);
      wipeSelectedCinemasIfNewLocation(selectedLocation);
      setCookie("location", selectedLocation.join(":"), {
        domain: cookieDomain,
        path: '/',
        maxAge: 31_556_926
      }); // 31 556 926 == 1 year
      if (!predefinedLocation) {
        updateUrl(selectedLocation.map(l => encodeURIComponent(l)).join(":"), router);
      }
      // Notify UserContext of location change
      userContext.updatePreferredLocation(selectedLocation);
      if (predefinedLocation && selectedLocation.some(sl => sl !== predefinedLocation)) {
        // at least one selected location is not the same as the predefined => redirect to generic program page
        router.push("/program");
      }
    }
    // NOTE: We cannot have router in deps, because that will cause infinite loop on pages where location is relevant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, userContext.updatePreferredLocation, predefinedLocation]);
  const _addLocation = useCallback((location: string) => {
    const selLoc = selectedLocation ?? [];
    const loc = Array.from(new Set([...selLoc, location])).sort();
    setSelectedLocation(loc);
  }, [selectedLocation]);
  const _removeLocation = useCallback((location: string) => {
    const selLoc = selectedLocation ?? [];
    const loc = selLoc.filter(l => l !== location).sort();
    setSelectedLocation(loc);
  }, [selectedLocation]);
  return <LocationContext.Provider value={{
    location: selectedLocation,
    setLocation: setSelectedLocation,
    addLocation: _addLocation,
    removeLocation: _removeLocation,
    showLocationDialog: showLocationDialog,
    setShowLocationDialog: setShowLocationDialog,
    isReady: isReady,
    showAllDates: showAllDates,
    firmId: null
  }}>
        {children}
        {showLocationDialog && <>
            {locationSelector && <>{locationSelector}</>}
            {!locationSelector && <LocationSelector canUseGeoLocation={canUseGeoLocation} />}
        </>}
    </LocationContext.Provider>;
}
//#endregion

//#region [Other]
/**
 * The selected cinemas belong to a location, so the cinemas cookie is wiped when a new location is saved.
 * Compared as sets: the saved location cookie can be in another order than the selected location.
 * Only reads the location cookie - it is written as before.
 */
function wipeSelectedCinemasIfNewLocation(locations: string[]) {
  const saved = (getCookie("location") ?? "").split(":").filter(l => l.trim() !== "").sort().join(":");
  if (saved !== [...locations].sort().join(":")) {
    deleteCookie(SELECTED_CINEMAS_COOKIE, {
      path: "/"
    });
  }
}
function updateUrl(loc: string, router: ReturnType<typeof useRouter>) {
  if (!loc?.trim()) {
    return;
  }
  if (typeof window !== "undefined") {
    if (ASK_FOR_LOCATION.filter(re => re.test(window.location.pathname)).length === 0) {
      // don't update the url if we are not on page that actually needs it
      return;
    }
    const search = window.location.search;
    let newSearch = "?";
    if (search) {
      const re = /(?:location=.*?(&)|location=.*$)/i;
      if (re.test(search)) {
        newSearch = search.replace(re, `location=${loc}$1`);
      } else {
        newSearch = search + "&location=" + loc;
      }
    } else {
      newSearch += "location=" + loc;
    }
    let newUrl = window.location.pathname + newSearch;
    if (window.location.hash) {
      newUrl += window.location.hash;
    }
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;
    if (newUrl === currentUrl) {
      // already reflects this location - avoid re-triggering a navigation (and everything that
      // listens to useSearchParams()) when this effect re-runs for an unrelated reason
      return;
    }
    console.debug("New url", newUrl);
    router.replace(newUrl, {
      scroll: false
    });
  }
}
function checkIfAllDatesShouldBeShown(selectedLocations: string[]) {
  if ((selectedLocations?.length ?? 0) === 0) {
    return false;
  }
  if (BRAND === "kinodk") {
    return false;
  }
  return selectedLocations.every(loc => LOCATIONS_SMALL.includes(loc.toLowerCase()));
}
//#endregion